

import _ from "lodash";

import { syncTimerTemplates } from "../actions/inventoryActions";
import env from "../constants/env";
import browserHistory from "../history";
import { getJsonFileFromS3, printTemplate, putJsonFileToS3, saveHistory, sendEmail } from "../persistence/s3";
import state from "../state/state";
import { refreshCredentials } from "../utils/securityUtil";
import {
	getLs,
	normalizeActivity,
	setFullSessionName,
	recurseReplaceSpeeds,
	recurseConvertChecklistToMarkdown,
	getQueryStringParam,
} from "../utils/utils";
import { logInAppConsole } from "../utils/webLoginSequenceLogger";

import { showError, showSuccess } from "./alertActions";
import { getDriveObjectsAsMap } from "./drive";
import { assembleActivitiesForMobile } from "./inventoryActions";
import { updateItunesSubscription, logout } from "./portalActions";
import { getIdentityId, getUsername, isOrganizationUser } from "./user";
import { getOrgInfo, getOrgId, updateMemberInfoInDb } from "./orgsActions";

import { navigateTo } from "./navActions";

import { publishActivityToSignedUrls } from "./publish";
import { getAssignmentScheduleForCurrentUser, getHealthStreamAssignmentScheduleForCurrentUser, refreshHealthStreamLearningForCurrentUser } from "./assignmentActions";
import { mergePricingInvoiceTaskState } from "../utils/invoiceTaskUtils";
import { isPricingIframeMessageForSession } from "../utils/pricingIframeMessageUtils";
import { validatePricingChecklistForSession } from "../utils/pricingSessionUtils";
import { createEmptyHealthStreamLearning, HCA_HEALTHSTREAM_ORG_ID, normalizeHealthStreamLearningResponse } from "../utils/healthStreamLearning";
import { isHealthStreamFutureEnabled } from "../utils/featureFlags";

const authorSkuSelectionListeners = new Set();
const authorSkuFilterListeners = new Set();
const MOBILE_ORG_INFO_FIELDS = ["id", "name", "logoUrl", "preferences"];
const HCA_HOSTNAME = "hca.ambifi.com";
const HCA_NATIVE_WEBVIEW_SEEN_SS = "hcaNativeMobileWebViewSeen";
const HEALTHSTREAM_LEARNING_MOBILE_REQUEST_VERSION = 2;
const LEGACY_HEALTHSTREAM_REDACTED_FIELDS = new Set([
	"courseurl",
	"healthstreamcourseurl",
	"ishealthstreamlaunchable"
]);

function getNormalizedLegacyHealthStreamFieldName(fieldName) {
	return _.toString(fieldName).replace(/[_-]/g, "").toLowerCase();
}

export function sanitizeHealthStreamAssignmentsForLegacyMobile(value) {
	if (_.isArray(value)) {
		return value.map(sanitizeHealthStreamAssignmentsForLegacyMobile);
	}

	if (!_.isPlainObject(value)) {
		return value;
	}

	return _.reduce(value, (sanitized, childValue, fieldName) => {
		if (!LEGACY_HEALTHSTREAM_REDACTED_FIELDS.has(getNormalizedLegacyHealthStreamFieldName(fieldName))) {
			sanitized[fieldName] = sanitizeHealthStreamAssignmentsForLegacyMobile(childValue);
		}
		return sanitized;
	}, {});
}

export function isHealthStreamLearningV2Request(message = {}) {
	return _.isPlainObject(message)
		&& message.method === "refreshHealthStreamLearning"
		&& Number(message.version) >= HEALTHSTREAM_LEARNING_MOBILE_REQUEST_VERSION;
}

function hasNativeMobileBridge() {
	return !!(
		typeof window !== "undefined" &&
		(
			(window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") ||
			(window.webView && typeof window.webView.postMessage === "function")
		)
	);
}

function hasNonEmptyJobDetails(jobDetails = null) {
	if (!jobDetails || typeof jobDetails !== "object") {
		return false;
	}
	return ["name", "description", "instructions", "notes", "parts"].some(key => _.toString(jobDetails[key] || "").trim() !== "");
}

function getNativeMobileWebViewSeenForSession() {
	try {
		return typeof window !== "undefined" && window.sessionStorage && window.sessionStorage.getItem(HCA_NATIVE_WEBVIEW_SEEN_SS) === "true";
	} catch (_err) {
		return false;
	}
}

function setNativeMobileWebViewSeenForSession() {
	try {
		if (typeof window !== "undefined" && window.sessionStorage) {
			window.sessionStorage.setItem(HCA_NATIVE_WEBVIEW_SEEN_SS, "true");
		}
	} catch (_err) {
		// Session-only native marker; safe to ignore.
	}
}

function hasCurrentRequestNativeMobileMarker() {
	return getQueryStringParam("app-api-version") !== ""
		|| getQueryStringParam("nativeSsoLogin") !== ""
		|| getQueryStringParam("nativeSsoLoginRequestId") !== "";
}

function isNativeHcaSsoHost() {
	return !!(
		typeof window !== "undefined"
		&& window.location
		&& window.location.hostname
		&& window.location.hostname.toLowerCase() === HCA_HOSTNAME
		&& (hasNativeMobileBridge() || hasCurrentRequestNativeMobileMarker() || getNativeMobileWebViewSeenForSession())
	);
}

function markNativeMobileWebViewSeen(reason = "") {
	if (typeof window === "undefined") {
		return;
	}

	const isHcaHost = window.location && window.location.hostname && window.location.hostname.toLowerCase() === HCA_HOSTNAME;
	const nativeSignal = hasNativeMobileBridge()
		|| hasCurrentRequestNativeMobileMarker()
		|| reason === "receivedMessageFromRN"
		|| reason === "document-message";
	if (isHcaHost && nativeSignal) {
		setNativeMobileWebViewSeenForSession();
	}
	state.set(["appState", "nativeMobileWebViewSeen"], true);
	if (reason) {
		state.set(["appState", "nativeMobileWebViewSeenReason"], reason);
	}
}

function handleNativeHcaBootstrapUserMessage(user = {}) {
	if (!isNativeHcaSsoHost()) {
		return false;
	}

	if (_.isPlainObject(user) && Object.prototype.hasOwnProperty.call(user, "sharedDevice")) {
		state.set(["user", "sharedDevice"], !!user.sharedDevice);
	}

	sendDebugLogMessage("Cloud ignored native credential bootstrap on HCA SSO host", {
		hostname: window.location.hostname,
		hasUsername: !!(user && user.username),
		hasPassword: !!(user && user.password),
		sharedDevice: !!(user && user.sharedDevice)
	});
	return true;
}

function handleExternalSsoCanceledFromNative(meta = {}) {
	if (!isNativeHcaSsoHost()) {
		return false;
	}

	state.set(["appState", "nativeHcaSsoRedirectPending"], false);
	state.set(["appState", "loading"], false);
	state.set(["appState", "loadingMessage"], "");
	state.set(["restoreSession"], false);
	sendDebugLogMessage("Cloud cleared HCA SSO redirect after native cancel", {
		requestId: meta.requestId || "",
		resultType: meta.resultType || "cancel"
	});
	postMessage({
		method: "loginStatus",
		label: "SSO login canceled."
	});
	return true;
}

function resolveSelectedPricingPayloadInvoiceItems(pricing = {}) {
	if (!pricing || typeof pricing !== "object") {
		return null;
	}

	const pricingContractProto = pricing.pricingContractProto;
	if (pricingContractProto && Array.isArray(pricingContractProto.documents) && pricingContractProto.documents.length > 0) {
		const activeDocument = pricingContractProto.documents.find((document) => document?.id === pricingContractProto.activeDocumentId)
			|| pricingContractProto.documents[pricingContractProto.documents.length - 1];
		if (activeDocument) {
			return Array.isArray(activeDocument.invoiceItems) ? activeDocument.invoiceItems : [];
		}
	}

	if (Array.isArray(pricing.estimates) && pricing.selectedValue) {
		const selectedEstimate = pricing.estimates.find((estimate) => estimate?.value === pricing.selectedValue);
		if (selectedEstimate) {
			return Array.isArray(selectedEstimate.invoiceItems) ? selectedEstimate.invoiceItems : [];
		}
	}

	return null;
}

function hasManagedPricingContractProto(pricingContractProto) {
	return Boolean(
		pricingContractProto
		&& Array.isArray(pricingContractProto.documents)
		&& pricingContractProto.documents.length > 0
	);
}

function emitMobilePerfSample(label, durationMs, meta = {}) {
	if (typeof window === "undefined" || !window.ReactNativeWebView || typeof window.ReactNativeWebView.postMessage !== "function") {
		return;
	}

	try {
		postMessage({
			method: "perfSample",
			sample: {
				source: "cloud-webview",
				label,
				durationMs,
				meta,
				timestamp: Date.now()
			}
		});
	} catch (err) {
		console.error("Unable to emit mobile perf sample", err);
	}
}

function startPerfTimer(label, meta = null) {
	const start = Date.now();
	if (meta) {
		console.log(`[perf] ${label}: start`, meta);
	} else {
		console.log(`[perf] ${label}: start`);
	}

	return (extraMeta = null) => {
		const durationMs = Date.now() - start;
		const mergedMeta = _.assign({}, meta || {}, extraMeta || {});
		if (meta || extraMeta) {
			console.log(`[perf] ${label}: ${durationMs}ms`, mergedMeta);
		} else {
			console.log(`[perf] ${label}: ${durationMs}ms`);
		}
		emitMobilePerfSample(label, durationMs, mergedMeta);
		return durationMs;
	};
}

export function registerAuthorSkuSelectionListener(handler) {
	if (typeof handler === "function") {
		authorSkuSelectionListeners.add(handler);
	}
}

export function unregisterAuthorSkuSelectionListener(handler) {
	if (handler && authorSkuSelectionListeners.has(handler)) {
		authorSkuSelectionListeners.delete(handler);
	}
}

export function registerAuthorSkuFilterListener(handler) {
	if (typeof handler === "function") {
		authorSkuFilterListeners.add(handler);
	}
}

export function unregisterAuthorSkuFilterListener(handler) {
	if (handler && authorSkuFilterListeners.has(handler)) {
		authorSkuFilterListeners.delete(handler);
	}
}

function normalizeMemberInfo(memberInfoRaw) {
	if (_.isPlainObject(memberInfoRaw)) {
		return _.cloneDeep(memberInfoRaw);
	}

	if (_.isString(memberInfoRaw) && _.trim(memberInfoRaw) !== "") {
		try {
			const parsed = JSON.parse(memberInfoRaw);
			return _.isPlainObject(parsed) ? parsed : {};
		} catch (err) {
			console.warn("Unable to parse memberInfo for mobile bridge", err);
		}
	}

	return {};
}

function getCurrentMemberInfo() {
	return normalizeMemberInfo(_.get(state.get(["selectedOrg", "member"]), "memberInfo"));
}

function getCurrentMemberMobileUiPrefs() {
	const memberInfo = getCurrentMemberInfo();
	return _.isPlainObject(memberInfo.mobileUiPrefs) ? _.cloneDeep(memberInfo.mobileUiPrefs) : {};
}

async function updateCurrentMemberMobileUiPrefs(mobileUiPrefs = {}) {
	const member = state.get(["selectedOrg", "member"]);
	if (!member || !member.id || !member.userSubId || !_.isPlainObject(mobileUiPrefs)) {
		return false;
	}

	const nextMobileUiPrefs = _.assign({}, getCurrentMemberMobileUiPrefs(), mobileUiPrefs);
	const nextMemberInfo = _.assign({}, getCurrentMemberInfo(), {
		mobileUiPrefs: nextMobileUiPrefs
	});
	const serializedMemberInfo = JSON.stringify(nextMemberInfo);

	await updateMemberInfoInDb(member.id, nextMemberInfo);

	state.set(["selectedOrg", "member", "memberInfo"], serializedMemberInfo);
	if (state.get(["user", "member"])) {
		state.set(["user", "member", "memberInfo"], serializedMemberInfo);
	}

	return true;
}

function getNativeBridgeCapabilitySnapshot(extra = {}) {
	return _.assign({
		hasReactNativeWebViewPostMessage: !!(
			typeof window !== "undefined" &&
			window.ReactNativeWebView &&
			typeof window.ReactNativeWebView.postMessage === "function"
		),
		hasWebViewPostMessage: !!(
			typeof window !== "undefined" &&
			window.webView &&
			typeof window.webView.postMessage === "function"
		),
		hasWindowPostMessage: !!(
			typeof window !== "undefined" &&
			typeof window.postMessage === "function"
		),
		appApiVersion: getQueryStringParam("app-api-version") || "",
		nativeCloudPortal: getQueryStringParam("nativeCloudPortal") || "",
		nativeSsoLogin: getQueryStringParam("nativeSsoLogin") || "",
		hasNativeSsoLoginRequestId: getQueryStringParam("nativeSsoLoginRequestId") !== "",
		hasNativeAppLaunchId: getQueryStringParam("nativeAppLaunchId") !== "",
		nativeMobileWebViewSeen: getNativeMobileWebViewSeenForSession()
	}, extra || {});
}

function getNativeMessagePayload(rawMessage, unwrapData = false) {
	const payload = unwrapData && rawMessage && Object.prototype.hasOwnProperty.call(rawMessage, "data")
		? rawMessage.data
		: rawMessage;

	if (_.isString(payload)) {
		const trimmedPayload = _.trim(payload);
		if (trimmedPayload === "") {
			return null;
		}

		try {
			return JSON.parse(trimmedPayload);
		} catch (_err) {
			return payload;
		}
	}

	return payload;
}

function getNativeMessageShape(value) {
	if (_.isArray(value)) {
		return "array";
	}

	if (_.isPlainObject(value)) {
		return "object";
	}

	if (_.isNil(value)) {
		return "empty";
	}

	return typeof value;
}

function logNativeBridgeMessageReceived(source, rawMessage, payload) {
	const payloadIsObject = _.isPlainObject(payload);
	logInAppConsole("mobile.bridge.message received", getNativeBridgeCapabilitySnapshot({
		source,
		rawShape: getNativeMessageShape(rawMessage),
		payloadShape: getNativeMessageShape(payload),
		method: payloadIsObject ? payload.method || "" : "",
		hasUsername: payloadIsObject && !!payload.username,
		hasPassword: payloadIsObject && !!payload.password,
		hasSharedDevice: payloadIsObject && Object.prototype.hasOwnProperty.call(payload, "sharedDevice"),
		keyCount: payloadIsObject ? _.keys(payload).length : 0
	}));
}

function installNativeBridgeDebugGlobal() {
	if (typeof window === "undefined") {
		return;
	}

	window.__AMBIFI_NATIVE_BRIDGE__ = {
		version: "2026-06-01-web-controlled-bridge-v1",
		getCapabilities: () => getNativeBridgeCapabilitySnapshot(),
		postMessage: (message) => postMessage(message)
	};
}

function sendNativeBridgeCapabilities(source = "unknown") {
	const capabilities = getNativeBridgeCapabilitySnapshot({
		source
	});
	logInAppConsole("mobile.bridge.capabilities", capabilities);
	postMessage({
		method: "bridgeCapabilities",
		source: "cloud-webview",
		timestamp: Date.now(),
		capabilities
	});
}

function handleSetPlaylistIdFromNative(obj, source = "unknown") {
	const playlistId = obj.id || obj.playlistId || "";
	const requestId = obj.requestId || obj.syncRequestId || `playlist-${Date.now()}`;
	const syncRequestId = obj.syncRequestId || requestId;
	const payloadMeta = {
		source: obj.source || "playlistChange",
		syncRequestId,
		requestId,
		hideUi: Object.prototype.hasOwnProperty.call(obj, "hideUi") ? !!obj.hideUi : true,
		showTransferPanel: Object.prototype.hasOwnProperty.call(obj, "showTransferPanel") ? !!obj.showTransferPanel : false,
		suppressGlobalSpinner: Object.prototype.hasOwnProperty.call(obj, "suppressGlobalSpinner") ? !!obj.suppressGlobalSpinner : true,
		keepPlaylistSearchVisibleUntilLoaded: !!obj.keepPlaylistSearchVisibleUntilLoaded,
		showSelectionSpinner: !!obj.showSelectionSpinner,
		closePlaylistSearchAfterLoad: !!obj.closePlaylistSearchAfterLoad
	};
	logInAppConsole("mobile.setPlaylistId received", {
		source,
		playlistId,
		syncRequestId,
		requestId,
		hideUi: payloadMeta.hideUi,
		showTransferPanel: payloadMeta.showTransferPanel,
		suppressGlobalSpinner: payloadMeta.suppressGlobalSpinner,
		keepPlaylistSearchVisibleUntilLoaded: payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
		showSelectionSpinner: payloadMeta.showSelectionSpinner,
		closePlaylistSearchAfterLoad: payloadMeta.closePlaylistSearchAfterLoad
	});
	refreshCredentials().then(() => {
		logInAppConsole("mobile.setPlaylistId credentials refreshed", {
			source,
			playlistId,
			syncRequestId,
			requestId
		});
		sendInventoryAndUserInfoMessage(null, playlistId, payloadMeta).then(() => {
			logInAppConsole("mobile.setPlaylistId inventory payload sent", {
				source,
				playlistId,
				syncRequestId,
				requestId
			});
		}).catch((err) => {
			logInAppConsole("mobile.setPlaylistId inventory payload failed", {
				source,
				playlistId,
				syncRequestId,
				requestId,
				error: err && err.message ? err.message : `${err}`
			});
			postMessage({ "error": "Get checklists failed: " + err.message });
		});
	}).catch(err => {
		logInAppConsole("mobile.setPlaylistId credentials failed", {
			source,
			playlistId,
			syncRequestId,
			requestId,
			error: err && err.message ? err.message : `${err}`
		});
		postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
	});
}

export function initializePostMessageListeners() {
	installNativeBridgeDebugGlobal();
	logInAppConsole("mobile.bridge.listeners initialized", getNativeBridgeCapabilitySnapshot());

	// WKWebView
	window.receivedMessageFromRN = async (e) => {
		markNativeMobileWebViewSeen("receivedMessageFromRN");
		console.log("receivedMessage: " + JSON.stringify(e));

		const shouldUnwrapReceivedMessageData = e
			&& Object.prototype.hasOwnProperty.call(e, "data")
			&& !Object.prototype.hasOwnProperty.call(e, "method");
		let obj = getNativeMessagePayload(e, shouldUnwrapReceivedMessageData);
		logNativeBridgeMessageReceived("receivedMessageFromRN", e, obj);
		if (!_.isObject(obj)) {
			logInAppConsole("mobile.bridge.message ignored", {
				source: "receivedMessageFromRN",
				payloadShape: getNativeMessageShape(obj)
			});
			return;
		}

		if (Array.isArray(obj)) {
			console.log("Call from mobile device (receivedMessageFromRN)", obj);
			// Compile checklists to send back to mobile app 
			refreshCredentials().then(() => {
				// sendInventoryAndUserInfoMessage().then(() => {
				return saveHistoryInstances(obj, {}, true);
				// }).catch((err) => {
				// postMessage({ "error": "Get checklists failed: " + err.message });
				// });
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});


		} else if (obj.hasOwnProperty("itunesSubscription")) {
			let sub = e;
			let itunesSubscription = {
				plan: sub.itunesSubscription,
				expiration: sub.subscriptionExpiration,
				transactionId: sub.subscriptionTransactionId
			};
			refreshCredentials().then(() => {
				updateItunesSubscription(itunesSubscription);
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("deviceId")) {
			let device = e;
			state.set(["device"], device);
		} else if (obj.hasOwnProperty("timerTemplates")) {
			const syncRequestId = obj.syncRequestId || "";
			sendDebugLogMessage("Cloud sync received timerTemplates request", {
				syncRequestId,
				refreshChecklists: !!obj.refreshChecklists,
				clientTemplateCount: _.isArray(obj.timerTemplates) ? obj.timerTemplates.length : 0
			});
			const endMobileTimerTemplateSync = startPerfTimer("cloud.mobile.timerTemplatesSync", {
				refreshChecklists: !!obj.refreshChecklists,
				clientTemplateCount: _.isArray(obj.timerTemplates) ? obj.timerTemplates.length : 0
			});
			const clientTimerTemplates = obj.timerTemplates;
			const endLoadServerTimerTemplates = startPerfTimer("cloud.mobile.timerTemplatesSync.loadServerTemplates");
			const serverTimerTemplates = await getJsonFileFromS3(env.s3.contentBucket, "timerTemplates.json");
			endLoadServerTimerTemplates({
				serverTemplateCount: _.isArray(serverTimerTemplates) ? serverTimerTemplates.length : 0
			});
			sendDebugLogMessage("Cloud sync loaded server timerTemplates", {
				syncRequestId,
				serverTemplateCount: _.isArray(serverTimerTemplates) ? serverTimerTemplates.length : 0
			});
			const timerTemplates = syncTimerTemplates(clientTimerTemplates, serverTimerTemplates);

			// Save to s3
			const endSaveTimerTemplates = startPerfTimer("cloud.mobile.timerTemplatesSync.saveServerTemplates", {
				mergedTemplateCount: _.isArray(timerTemplates) ? timerTemplates.length : 0
			});
			await putJsonFileToS3(env.s3.contentBucket, "timerTemplates.json", timerTemplates);
			endSaveTimerTemplates();
			sendDebugLogMessage("Cloud sync saved timerTemplates", {
				syncRequestId,
				mergedTemplateCount: _.isArray(timerTemplates) ? timerTemplates.length : 0
			});
			if (obj.refreshChecklists) {
				const endRefreshCredentials = startPerfTimer("cloud.mobile.timerTemplatesSync.refreshCredentials");
				refreshCredentials().then(() => {
					endRefreshCredentials();
					sendDebugLogMessage("Cloud sync refreshed credentials", {
						syncRequestId
					});
					sendInventoryAndUserInfoMessage(null, null, {
						source: "sync",
						syncRequestId
					}).then(() => {
						sendDebugLogMessage("Cloud sync sent inventory payload", {
							syncRequestId
						});
						endMobileTimerTemplateSync();
					}).catch((err) => {
						endMobileTimerTemplateSync({
							error: err && err.message ? err.message : `${err}`
						});
						sendDebugLogMessage("Cloud sync failed sending inventory payload", {
							syncRequestId,
							error: err && err.message ? err.message : `${err}`
						});
						postMessage({ "error": "Get checklists failed: " + err.message });
					});
				}).catch(err => {
					endMobileTimerTemplateSync({
						error: err && err.message ? err.message : `${err}`
					});
					sendDebugLogMessage("Cloud sync failed refreshing credentials", {
						syncRequestId,
						error: err && err.message ? err.message : `${err}`
					});
					postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
				});
			} else {
				endMobileTimerTemplateSync();
			}
		} else if (obj.hasOwnProperty("type") && obj.type === "checklist") {
			refreshCredentials().then(() => {
				saveHistoryInstances([obj]);
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("playlistId")) {
			handleSetPlaylistIdFromNative(obj, "receivedMessageFromRN:playlistId");
		} else if (obj.hasOwnProperty("method")) {
			switch (obj.method) {
				case "sendEmail": {
					await sendEmail(obj.toAddresses, obj.subject, obj.body, obj.htmlBody, obj.ccAddresses, obj.senderEmail, obj.replyToAddresses, obj.charset);
					break;
				}
				case "setPlaylistId": {
					handleSetPlaylistIdFromNative(obj, "receivedMessageFromRN");
					break;
				}
				case "getBridgeCapabilities": {
					sendNativeBridgeCapabilities("receivedMessageFromRN");
					break;
				}
				case "navigateTo": {
					navigateTo(obj.id);
					break;
				}
				case "logout": {
					sendDebugLogMessage("Cloud received logout command from native", {
						username: state.get(["user", "username"]) || ""
					});
					logout();
					break;
				}
				case "externalSsoCanceled": {
					handleExternalSsoCanceledFromNative({
						requestId: obj.requestId || "",
						resultType: obj.resultType || "cancel"
					});
					break;
				}
				case "setSharedDevice": {
					state.set(["user", "sharedDevice"], !!obj.sharedDevice);
					sendDebugLogMessage("Cloud received shared device flag from native", {
						sharedDevice: !!obj.sharedDevice
					});
					break;
				}
				case "refreshAssignments": {
					const syncRequestId = obj.syncRequestId || "";
					sendDebugLogMessage("Cloud sync received refreshAssignments request", {
						syncRequestId
					});
					getAssignmentScheduleForCurrentUser().then(assignments => {
						sendDebugLogMessage("Cloud sync sent assignments payload", {
							syncRequestId,
							assignmentCount: _.isArray(assignments) ? assignments.length : 0
						});
						sendAssignmentsMessage(assignments);
					}).catch(err => {
						sendDebugLogMessage("Cloud sync failed refreshing assignments", {
							syncRequestId,
							error: err && err.message ? err.message : `${err}`
						});
					});
					break;
				}
				case "refreshHealthStreamAssignments": {
					if (getOrgId() === HCA_HEALTHSTREAM_ORG_ID) {
						refreshAndSendHealthStreamLearning();
					}
					break;
				}
				case "refreshHealthStreamLearning": {
					if (getOrgId() === HCA_HEALTHSTREAM_ORG_ID) {
						refreshAndSendHealthStreamLearning({
							includeV2: isHealthStreamLearningV2Request(obj)
						});
					}
					break;
				}
				case "updateMobileUiPrefs": {
					try {
						await updateCurrentMemberMobileUiPrefs(obj.mobileUiPrefs);
					} catch (err) {
						console.error("Unable to update mobile UI prefs", err);
					}
					break;
				}
				case "saveInCameraRoll": {
					if (obj.success) {
						showSuccess("Save Media Asset", "Successfully saved media asset in camera roll.");
					} else {
						showError("Save Media Asset", "Local save of media asset failed, reason:" + obj.err);
					}
					break;
				}
				case "printInstance": {
					console.log("!*!*!* printInstance");
					// Set previewChecklist
					let jsonChecklist = obj.data;

					let lastTimestamp = (jsonChecklist.history[0].timestamp).replace(" ", "T");
					let fileName = jsonChecklist.id + "/" + lastTimestamp + "_" + jsonChecklist.instanceId + ".json";

					jsonChecklist = await publishActivityToSignedUrls(jsonChecklist, fileName);

					// Should convert to make sure has at least one list and one section and set proper type
					jsonChecklist = normalizeActivity(jsonChecklist);
					jsonChecklist = setFullSessionName(jsonChecklist);

					recurseReplaceSpeeds(jsonChecklist.children);
					recurseConvertChecklistToMarkdown(jsonChecklist.children, false);

					printTemplate(obj.name, jsonChecklist, obj.templateId, obj.callbackJsonToken);
					break;
				}
				default: {
					console.log("Nothing to do");
				}
			}
		} else {

			let user = e;
			if (handleNativeHcaBootstrapUserMessage(user)) {
				return;
			}

			let savePassword = getLs(user.username + ";savePassword");

			if (savePassword === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					savePassword = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					savePassword = true;
				}
			}

			state.set(["user", "username"], user.username);
			if (savePassword) {
				state.set(["user", "password"], user.password);
			}
			state.set(["user", "savePassword"], savePassword);

			let rememberMe = getLs(user.username + ";rememberMe");
			if (rememberMe === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					rememberMe = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					rememberMe = true;
				}
			}

			state.set(["user", "rememberMe"], rememberMe);

			console.log("index.js: " + JSON.stringify(state.get(["user"])));

			const path = "/login";
			browserHistory.push(path);
		}
	};

	window.addEventListener("message", (e) => {
		try {
			let data = e.data;
			if (data && typeof data === "string") {
				try {
					data = JSON.parse(data);
				} catch (err) {
					data = null;
				}
			}
			const isAuthorSkuMessage = data && data.clientItemId === "authorSkuPicker";
			if (isAuthorSkuMessage && (data.method === "addSearchHit" || data.method === "removeSkuSelection" || data.method === "setSkuSelections" || data.method === "closeSkuSearchModal")) {
				authorSkuSelectionListeners.forEach((handler) => {
					try {
						handler(data);
					} catch (err) {
						// ignore individual handler errors
					}
				});
				return;
			}
			if (isAuthorSkuMessage && data.method === "setFilterExpression") {
				authorSkuFilterListeners.forEach((handler) => {
					try {
						handler(data);
					} catch (err) {
						// ignore individual handler errors
					}
				});
				return;
			}
			if (data && data.pricing && data.pricing.invoiceItems) {
				const pricingState = state.get(["appState", "showPricing"]) || {};
				const pricingSession = pricingState.session;
				const messageSessionId = typeof data.pricingSessionId === "string" ? data.pricingSessionId : "";
				if (!pricingState.showModal
					|| !pricingSession
					|| !isPricingIframeMessageForSession(e, pricingSession.sessionId, messageSessionId)) {
					console.warn("Ignoring pricing state update for an inactive or unrecognized pricing session.");
					return;
				}
				const pricingChecklist = pricingState.checklist || {};
				const identityValidation = validatePricingChecklistForSession(pricingChecklist, pricingSession);
				if (!identityValidation.valid) {
					console.warn("Ignoring pricing state update because the active job identity is not valid.", identityValidation.reason);
					return;
				}
				const incomingItems = data.pricing.invoiceItems || [];
				const existingItems = pricingState.invoiceItems || [];
				const resolvedPricingItems = resolveSelectedPricingPayloadInvoiceItems(data.pricing);
				const checklist = pricingChecklist;
				const existingEstimates = [
					state.get(["appState", "showPricing", "estimatesTemp"]),
					state.get(["appState", "showPricing", "estimates"]),
					checklist.estimates
				].find(estimates => Array.isArray(estimates) && estimates.length > 0) || [];
				const incomingEstimates = Array.isArray(data.pricing.estimates) ? data.pricing.estimates : null;
				const shouldPreserveExistingEstimates = data.pricing.forceClear !== true
					&& (!incomingEstimates || incomingEstimates.length === 0)
					&& existingEstimates.length > 0;
				const existingSelectedValue = _.toString(
					state.get(["appState", "showPricing", "selectedValueTemp"])
					|| state.get(["appState", "showPricing", "selectedValue"])
					|| checklist.selectedValue
					|| ""
				).trim();
				const incomingSelectedValue = _.toString(data.pricing.selectedValue || "").trim();
				const resolvedSelectedValue = shouldPreserveExistingEstimates && !incomingSelectedValue
					? existingSelectedValue
					: data.pricing.selectedValue;
				const pricingStateEstimates = shouldPreserveExistingEstimates ? existingEstimates : data.pricing.estimates;
				const incomingPricingState = mergePricingInvoiceTaskState({
					selectedValue: resolvedSelectedValue,
					invoiceItems: incomingItems,
					estimates: pricingStateEstimates,
					pricingContractProto: data.pricing.pricingContractProto || null
				}, {
					selectedValue: resolvedSelectedValue || checklist.selectedValue,
					invoiceItems: checklist.invoiceItems || [],
					estimates: checklist.estimates || existingEstimates || [],
					pricingContractProto: checklist.pricingContractProto || null
				});
				const allowClear = data.pricing.forceClear === true
					|| (
						Array.isArray(incomingItems)
						&& incomingItems.length === 0
						&& Array.isArray(resolvedPricingItems)
						&& resolvedPricingItems.length === 0
						&& (!Array.isArray(existingItems) || existingItems.length === 0)
						&& existingEstimates.length === 0
					);
				const skipInvoiceItems = !allowClear && Array.isArray(incomingItems) && incomingItems.length === 0 && Array.isArray(existingItems) && existingItems.length > 0;
				if (!skipInvoiceItems) {
					state.set(["appState", "showPricing", "invoiceItemsTemp"], incomingPricingState.invoiceItems || incomingItems);
				}
				state.set(["appState", "showPricing", "estimatesTemp"], incomingPricingState.estimates || []);
				const defaultJobDetails = { name: "", description: "", instructions: "", notes: "", parts: "" };
				const existingJobDetails = state.get(["appState", "showPricing", "jobDetailsTemp"])
					|| state.get(["appState", "showPricing", "jobDetails"])
					|| state.get(["appState", "showPricing", "checklist", "jobDetails"])
					|| null;
				const incomingJobDetails = data.pricing.jobDetails;
				const shouldPreserveExistingJobDetails = hasNonEmptyJobDetails(existingJobDetails)
					&& !hasNonEmptyJobDetails(incomingJobDetails)
					&& data.pricing.forceClear !== true;
				state.set(
					["appState", "showPricing", "jobDetailsTemp"],
					shouldPreserveExistingJobDetails ? existingJobDetails : (incomingJobDetails || existingJobDetails || defaultJobDetails)
				);
				state.set(["appState", "showPricing", "selectedValueTemp"], resolvedSelectedValue);
				if (data.pricing.projectOps || data.pricing.projectContractAnchor || data.pricing.workflowLaunch || data.pricing.activityOps) {
					state.set(["appState", "showPricing", "projectOpsContext"], {
						projectOps: data.pricing.projectOps || null,
						projectContractAnchor: data.pricing.projectContractAnchor || null,
						workflowLaunch: data.pricing.workflowLaunch || null,
						activityOps: data.pricing.activityOps || null
					});
				}
				if (Object.prototype.hasOwnProperty.call(data.pricing, "pricingSnapshotId")) {
					state.set(["appState", "showPricing", "pricingSnapshotIdTemp"], data.pricing.pricingSnapshotId || "base");
				}
				if (Object.prototype.hasOwnProperty.call(data.pricing, "pricingMode")) {
					state.set(["appState", "showPricing", "pricingModeTemp"], data.pricing.pricingMode || "wholesale");
				}
				if (Object.prototype.hasOwnProperty.call(data.pricing, "pricingContractProto")) {
					const incomingPricingContractProto = incomingPricingState.pricingContractProto || null;
					const existingPricingContractProto = state.get(["appState", "showPricing", "pricingContractProtoTemp"])
						|| state.get(["appState", "showPricing", "pricingContractProto"])
						|| state.get(["appState", "showPricing", "checklist", "pricingContractProto"])
						|| null;
					const shouldPreserveExistingPricingContractProto = (
						hasManagedPricingContractProto(existingPricingContractProto)
						&& !hasManagedPricingContractProto(incomingPricingContractProto)
					);
					if (!shouldPreserveExistingPricingContractProto) {
						state.set(["appState", "showPricing", "pricingContractProtoTemp"], incomingPricingContractProto);
					}
				}
			}
		} catch (err) {
			// ignored
		}
	});

	// UIWebView
	document.addEventListener("message", async (e) => {
		markNativeMobileWebViewSeen("document-message");
		console.log("!@#!@# DOCUMENT LISTENER");
		let obj = getNativeMessagePayload(e, true);
		logNativeBridgeMessageReceived("document-message", e && Object.prototype.hasOwnProperty.call(e, "data") ? e.data : e, obj);
		if (!_.isObject(obj)) {
			logInAppConsole("mobile.bridge.message ignored", {
				source: "document-message",
				payloadShape: getNativeMessageShape(obj)
			});
			return;
		}
		if (Array.isArray(obj)) {
			console.log("Call from mobile device (receivedMessageFromRN)", obj);
			// Compile checklists to send back to mobile app 
			refreshCredentials().then(() => {
				// sendInventoryAndUserInfoMessage().then(() => {
				return saveHistoryInstances(obj, {}, true);
				// }).catch((err) => {
				// postMessage({ "error": "Get checklists failed: " + err.message });
				// });
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("pricing")) {
			console.log("!@#!@# MESSAGE 2", JSON.stringify(obj));
		} else if (obj.hasOwnProperty("deviceId")) {
			let device = obj;
			state.set(["device"], device);
		} else if (obj.hasOwnProperty("timerTemplates")) {
			const clientTimerTemplates = obj.timerTemplates;
			const serverTimerTemplates = await getJsonFileFromS3(env.s3.contentBucket, "timerTemplates.json");
			const timerTemplates = syncTimerTemplates(clientTimerTemplates, serverTimerTemplates);

			// Save to s3
			await putJsonFileToS3(env.s3.contentBucket, "timerTemplates.json", timerTemplates);
			if (obj.refreshChecklists) {
				refreshCredentials().then(() => {
					sendInventoryAndUserInfoMessage(null, null, {
						source: "sync",
						syncRequestId: obj.syncRequestId || ""
					}).then(() => {
					}).catch((err) => {
						postMessage({ "error": "Get checklists failed: " + err.message });
					});
				}).catch(err => {
					postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
				});
			}
		} else if (obj.hasOwnProperty("type") && obj.type === "checklist") {
			refreshCredentials().then(() => {
				saveHistoryInstances([obj]);
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("playlistId")) {
			handleSetPlaylistIdFromNative(obj, "document-message:playlistId");
		} else if (obj.hasOwnProperty("method")) {
			switch (obj.method) {
				case "setPlaylistId": {
					handleSetPlaylistIdFromNative(obj, "document-message");
					break;
				}
				case "getBridgeCapabilities": {
					sendNativeBridgeCapabilities("document-message");
					break;
				}
				case "navigateTo": {
					navigateTo(obj.id);
					break;
				}
				case "logout": {
					sendDebugLogMessage("Cloud received logout command from native", {
						source: "document-message",
						username: state.get(["user", "username"]) || ""
					});
					logout();
					break;
				}
				case "externalSsoCanceled": {
					handleExternalSsoCanceledFromNative({
						requestId: obj.requestId || "",
						resultType: obj.resultType || "cancel"
					});
					break;
				}
				case "setSharedDevice": {
					state.set(["user", "sharedDevice"], !!obj.sharedDevice);
					sendDebugLogMessage("Cloud received shared device flag from native", {
						source: "document-message",
						sharedDevice: !!obj.sharedDevice
					});
					break;
				}
				case "refreshAssignments": {
					getAssignmentScheduleForCurrentUser().then(assignments => {
						sendAssignmentsMessage(assignments);
					});
					break;
				}
				case "refreshHealthStreamAssignments": {
					if (getOrgId() === HCA_HEALTHSTREAM_ORG_ID) {
						refreshAndSendHealthStreamLearning();
					}
					break;
				}
				case "refreshHealthStreamLearning": {
					if (getOrgId() === HCA_HEALTHSTREAM_ORG_ID) {
						refreshAndSendHealthStreamLearning({
							includeV2: isHealthStreamLearningV2Request(obj)
						});
					}
					break;
				}
				case "updateMobileUiPrefs": {
					try {
						await updateCurrentMemberMobileUiPrefs(obj.mobileUiPrefs);
					} catch (err) {
						console.error("Unable to update mobile UI prefs", err);
					}
					break;
				}
				case "saveInCameraRoll": {
					if (obj.success) {
						showSuccess("Save Media Asset", "Successfully saved media asset in camera roll.");
					} else {
						showError("Save Media Asset", "Local save of media asset failed, reason:" + obj.err);
					}
					break;
				}
				case "printInstance": {
					// Set previewChecklist
					let jsonChecklist = obj.data;

					let lastTimestamp = (jsonChecklist.history[0].timestamp).replace(" ", "T");
					let fileName = jsonChecklist.id + "/" + lastTimestamp + "_" + jsonChecklist.instanceId + ".json";

					jsonChecklist = await publishActivityToSignedUrls(jsonChecklist, fileName);

					// Should convert to make sure has at least one list and one section and set proper type
					jsonChecklist = normalizeActivity(jsonChecklist);
					jsonChecklist = setFullSessionName(jsonChecklist);

					recurseReplaceSpeeds(jsonChecklist.children);
					recurseConvertChecklistToMarkdown(jsonChecklist.children, false);

					printTemplate(obj.name, jsonChecklist, obj.templateId, obj.callbackJsonToken);
					break;
				}
				default: {
					console.log("Nothing to do");
				}
			}
		} else {
			let user = obj;
			if (handleNativeHcaBootstrapUserMessage(user)) {
				return;
			}

			let savePassword = getLs(user.username + ";savePassword");

			if (savePassword === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					savePassword = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					savePassword = true;
				}
			}

			state.set(["user", "username"], user.username);
			if (savePassword) {
				state.set(["user", "password"], user.password);
			}
			state.set(["user", "savePassword"], savePassword);

			let rememberMe = getLs(user.username + ";rememberMe");
			if (rememberMe === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					rememberMe = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					rememberMe = true;
				}
			}

			state.set(["user", "rememberMe"], rememberMe);

			console.log("index.js: " + JSON.stringify(state.get(["user"])));

			const path = "/login";
			browserHistory.push(path);
		}
	});

	if (hasNativeMobileBridge()) {
		markNativeMobileWebViewSeen("initializePostMessageListeners");
		const readyMeta = getNativeBridgeCapabilitySnapshot({
			hostname: window.location && window.location.hostname ? window.location.hostname : "",
			pathname: window.location && window.location.pathname ? window.location.pathname : ""
		});
		postMessage({
			method: "nativeMessageBridgeReady",
			source: "cloud-webview",
			timestamp: Date.now(),
			meta: readyMeta
		});
		sendDebugLogMessage("Cloud mobile message bridge ready", readyMeta);
	}
}

export function sendOrganizationInfoMessage(orgInfos) {
	const info = normalizeOrgInfoForMobile(_.first(orgInfos));
	let message = {
		type: "org-info",
		info
	};

	postMessage(message);
}

export function sendAssignmentsMessage(assignments) {
	// newChecklists is what pass to mobile app. Aggregate users, content and checklists
	let message = {
		type: "assignemnts-message",
		assignments
	};

	postMessage(message);
}


export function sendHealthStreamAssignmentsMessage(assignments) {
	let message = {
		type: "assignments-healthstream-message",
		assignments: sanitizeHealthStreamAssignmentsForLegacyMobile(assignments)
	};

	postMessage(message);
}

export function sendHealthStreamLearningMessage(learning) {
	const normalizedLearning = normalizeHealthStreamLearningResponse(learning);
	postMessage({
		type: "healthstream-learning-message",
		version: 2,
		learning: normalizedLearning
	});
}

async function refreshAndSendHealthStreamLearning({ includeV2 = false } = {}) {
	const futureLearningEnabled = includeV2
		&& isHealthStreamFutureEnabled(state.get(["appState", "urlParams"]));
	try {
		if (!futureLearningEnabled) {
			const assignments = await getHealthStreamAssignmentScheduleForCurrentUser();
			sendHealthStreamAssignmentsMessage(assignments);
			return;
		}

		const learning = await refreshHealthStreamLearningForCurrentUser();
		sendHealthStreamAssignmentsMessage(learning.assignedLearning);
		sendHealthStreamLearningMessage(learning);
	} catch (err) {
		console.error("Unable to refresh HealthStream learning for mobile", err);
		if (futureLearningEnabled) {
			const previousLearning = normalizeHealthStreamLearningResponse(state.get(["healthStreamLearning"]));
			sendHealthStreamLearningMessage(createEmptyHealthStreamLearning({
				...previousLearning,
				loading: false,
				error: "HealthStream learning could not be refreshed. Please try again."
			}));
		}
	}
}

export function saveHistoryInstances(obj, overrides = {}, refreshChecklists = false) {
	// Array of instances to save to s3
	let instances = obj;
	let files = [];
	let errLog = [];

	//			awsS3.logToCloudWatch(instances);

	for (let i = 0; i < instances.length; i++) {
		//alert("Error " + i);
		try {
			let instance = instances[i];
			let lastTimestamp = (instance.history[0].timestamp).replace(" ", "T");
			let fileName = instance.id + "/" + lastTimestamp + "_" + instance.instanceId + ".json";

			if (overrides.displayName) {
				instance.displayName = overrides.displayName;
			} else {
				const member = state.get(["selectedOrg", "member"]);
				instance.displayName = member.displayName;
			}

			files.push({ fileName: fileName, fileContent: instance });
		} catch (e) {
			errLog.push(e);
		}
	}

	if (errLog.length > 0) {
		//alert(JSON.stringify(errLog,null,2));
	}

	return saveHistory(env.s3.contentBucket, files, overrides.keyPrefixOverride, refreshChecklists);
}

export function sendInventoryAndUserInfoMessage(inventory = null, playlistId, nativePayloadMeta = {}) {
	return new Promise(async (resolve, reject) => {
		const payloadMeta = _.assign({
			source: "login",
			syncRequestId: "",
			requestId: "",
			hideUi: false,
			showTransferPanel: false,
			suppressGlobalSpinner: false,
			keepPlaylistSearchVisibleUntilLoaded: false,
			showSelectionSpinner: false,
			closePlaylistSearchAfterLoad: false
		}, nativePayloadMeta || {});
		if (payloadMeta.requestId === "" && payloadMeta.syncRequestId !== "") {
			payloadMeta.requestId = payloadMeta.syncRequestId;
		}
		const normalizedPlaylistId = playlistId || "";
		logInAppConsole("mobile.inventoryPayload requested", {
			inventoryProvided: _.isArray(inventory),
			providedInventoryCount: _.isArray(inventory) ? inventory.length : 0,
			playlistId: normalizedPlaylistId,
			payloadSource: payloadMeta.source || "",
			syncRequestId: payloadMeta.syncRequestId || "",
			requestId: payloadMeta.requestId || "",
			hideUi: !!payloadMeta.hideUi,
			showTransferPanel: !!payloadMeta.showTransferPanel,
			suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
			keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
			showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
			closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad
		});
		const endSendInventoryAndUserInfo = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage", {
			inventoryProvided: _.isArray(inventory),
			providedInventoryCount: _.isArray(inventory) ? inventory.length : 0,
			playlistId: normalizedPlaylistId,
			payloadSource: payloadMeta.source || "",
			syncRequestId: payloadMeta.syncRequestId || "",
			requestId: payloadMeta.requestId || ""
		});
		// newChecklists is what pass to mobile app. Aggregate users, content and checklists
		let message = {
			user: getUserInfo(),
			timerTemplates: [],
			checklists: [],
			nativePayloadMeta: payloadMeta,
			requestId: payloadMeta.requestId || "",
			syncRequestId: payloadMeta.syncRequestId || "",
			hideUi: !!payloadMeta.hideUi,
			showTransferPanel: !!payloadMeta.showTransferPanel,
			suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
			keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
			showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
			closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad
		};

		// Get timerTemplates
		try {
			const endLoadTimerTemplates = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage.loadTimerTemplates");
			const serverTimerTemplates = await getJsonFileFromS3(env.s3.contentBucket, "timerTemplates.json");
			message.timerTemplates = serverTimerTemplates;
			endLoadTimerTemplates({
				timerTemplateCount: _.isArray(serverTimerTemplates) ? serverTimerTemplates.length : 0
			});
		} catch (err) {
			console.log(err.message);
		}

		const apiVersion = getQueryStringParam("app-api-version");
		const deferInventory = getQueryStringParam("deferInventory") === true || getQueryStringParam("deferInventory") === 1 || getQueryStringParam("deferInventory") === "1" || getQueryStringParam("deferInventory") === "true" || getQueryStringParam("deferInventory") === "yes" || getQueryStringParam("deferInventory") === "on";

		if (isOrganizationUser() && apiVersion >= 8) {
			const orgInfo = await getOrgInfoForMobilePayload();
			message.orgInfo = orgInfo;

			// Send any env props
			let mobileAppEnv = {
				algolia: env.algolia,
				deferInventory
			};
			message.env = mobileAppEnv;

			sendDebugLogMessage("Cloud sync posting deferred inventory payload", {
				apiVersion,
				deferInventory,
				playlistId: normalizedPlaylistId,
				payloadSource: payloadMeta.source || "",
				syncRequestId: payloadMeta.syncRequestId || "",
				requestId: payloadMeta.requestId || "",
				hideUi: !!payloadMeta.hideUi,
				showTransferPanel: !!payloadMeta.showTransferPanel,
				suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
				keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
				showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
				closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
				checklistCount: _.isArray(message.checklists) ? message.checklists.length : 0,
				timerTemplateCount: _.isArray(message.timerTemplates) ? message.timerTemplates.length : 0
			});
			logInAppConsole("mobile.inventoryPayload posting deferred", {
				apiVersion,
				deferInventory,
				playlistId: normalizedPlaylistId,
				payloadSource: payloadMeta.source || "",
				syncRequestId: payloadMeta.syncRequestId || "",
				requestId: payloadMeta.requestId || "",
				hideUi: !!payloadMeta.hideUi,
				showTransferPanel: !!payloadMeta.showTransferPanel,
				suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
				keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
				showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
				closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
				checklistCount: _.isArray(message.checklists) ? message.checklists.length : 0,
				timerTemplateCount: _.isArray(message.timerTemplates) ? message.timerTemplates.length : 0
			});
			postMessage(message);
			const durationMs = endSendInventoryAndUserInfo({
				checklistCount: _.isArray(message.checklists) ? message.checklists.length : 0,
				deferredInventory: true
			});
			logInAppConsole("mobile.inventoryPayload posted deferred", {
				playlistId: normalizedPlaylistId,
				payloadSource: payloadMeta.source || "",
				syncRequestId: payloadMeta.syncRequestId || "",
				requestId: payloadMeta.requestId || "",
				hideUi: !!payloadMeta.hideUi,
				showTransferPanel: !!payloadMeta.showTransferPanel,
				suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
				keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
				showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
				closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
				durationMs,
				checklistCount: _.isArray(message.checklists) ? message.checklists.length : 0
			});
			resolve();
		} else {
			const endAssembleActivitiesForMobile = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage.assembleActivitiesForMobile");
			assembleActivitiesForMobile(inventory, playlistId).then(async (mobileInventory) => {
				endAssembleActivitiesForMobile({
					mobileInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0
				});
				// Now retrieve the content map to allow resolving of external resources on the fly
				// *** Going to fetch directly from mobile app
				if (apiVersion < 6) {
					message.externalResources = getDriveObjectsAsMap();
				}

				console.log("!#!#!# MOBILE INVENTORY", mobileInventory);

				// Add filter for invisible checklists
				const resultMobileInventory = mobileInventory.filter(activity => {
					if (activity.dependency) {
						return true;
					} else {
						if (!activity.hasOwnProperty("visible")) {
							return true;
						} else {
							return activity.visible;
						}
					}
				});

				console.log("!#!#!# RESULT MOBILE INVENTORY", resultMobileInventory);

				// const resultMobileInventory = mobileInventory.filter(activity => activity.visible !== false);

				message.checklists = resultMobileInventory;
				const orgInfo = await getOrgInfoForMobilePayload();
					message.orgInfo = orgInfo;

					// Send any env props
					let mobileAppEnv = {
						algolia: env.algolia,
						deferInventory
					};
					message.env = mobileAppEnv;

					sendDebugLogMessage("Cloud sync posting inventory payload", {
						apiVersion,
						deferInventory,
						playlistId: normalizedPlaylistId,
						payloadSource: payloadMeta.source || "",
						syncRequestId: payloadMeta.syncRequestId || "",
						requestId: payloadMeta.requestId || "",
						hideUi: !!payloadMeta.hideUi,
						showTransferPanel: !!payloadMeta.showTransferPanel,
						suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
						keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
						showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
						closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
						checklistCount: resultMobileInventory.length,
						totalInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0,
						timerTemplateCount: _.isArray(message.timerTemplates) ? message.timerTemplates.length : 0
					});
					logInAppConsole("mobile.inventoryPayload posting", {
						apiVersion,
						deferInventory,
						playlistId: normalizedPlaylistId,
						payloadSource: payloadMeta.source || "",
						syncRequestId: payloadMeta.syncRequestId || "",
						requestId: payloadMeta.requestId || "",
						hideUi: !!payloadMeta.hideUi,
						showTransferPanel: !!payloadMeta.showTransferPanel,
						suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
						keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
						showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
						closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
						checklistCount: resultMobileInventory.length,
						totalInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0,
						timerTemplateCount: _.isArray(message.timerTemplates) ? message.timerTemplates.length : 0
					});
					postMessage(message);
					const durationMs = endSendInventoryAndUserInfo({
						checklistCount: resultMobileInventory.length,
						totalInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0
					});
					logInAppConsole("mobile.inventoryPayload posted", {
						playlistId: normalizedPlaylistId,
						payloadSource: payloadMeta.source || "",
						syncRequestId: payloadMeta.syncRequestId || "",
						requestId: payloadMeta.requestId || "",
						hideUi: !!payloadMeta.hideUi,
						showTransferPanel: !!payloadMeta.showTransferPanel,
						suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
						keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
						showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
						closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
						durationMs,
						checklistCount: resultMobileInventory.length,
						totalInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0
					});
					resolve();

			}).catch(err => {
				const durationMs = endSendInventoryAndUserInfo({
					error: err && err.message ? err.message : `${err}`
				});
				logInAppConsole("mobile.inventoryPayload failed", {
					playlistId: normalizedPlaylistId,
					payloadSource: payloadMeta.source || "",
					syncRequestId: payloadMeta.syncRequestId || "",
					requestId: payloadMeta.requestId || "",
					hideUi: !!payloadMeta.hideUi,
					showTransferPanel: !!payloadMeta.showTransferPanel,
					suppressGlobalSpinner: !!payloadMeta.suppressGlobalSpinner,
					keepPlaylistSearchVisibleUntilLoaded: !!payloadMeta.keepPlaylistSearchVisibleUntilLoaded,
					showSelectionSpinner: !!payloadMeta.showSelectionSpinner,
					closePlaylistSearchAfterLoad: !!payloadMeta.closePlaylistSearchAfterLoad,
					durationMs,
					error: err && err.message ? err.message : `${err}`
				});
				reject(err);
			});
		}
	});
}

function normalizeOrgInfoForMobile(orgInfo) {
	if (!orgInfo || !_.isObject(orgInfo)) {
		return null;
	}

	return _.pick(orgInfo, MOBILE_ORG_INFO_FIELDS);
}

function getCachedOrgInfoForMobilePayload() {
	const selectedOrg = normalizeOrgInfoForMobile(state.get(["selectedOrg"]));
	if (selectedOrg && selectedOrg.id) {
		return selectedOrg;
	}

	return null;
}

async function getOrgInfoForMobilePayload() {
	const endGetOrgInfo = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage.getOrgInfo");
	const cachedOrgInfo = getCachedOrgInfoForMobilePayload();
	if (cachedOrgInfo) {
		endGetOrgInfo({
			source: "state",
			orgId: cachedOrgInfo.id
		});
		return cachedOrgInfo;
	}

	const orgInfo = normalizeOrgInfoForMobile(await getOrgInfo());
	endGetOrgInfo({
		source: "network",
		orgId: orgInfo && orgInfo.id ? orgInfo.id : ""
	});
	return orgInfo;
}

export function sendTokenUpdatedMessage(token) {
	state.set(["user", "jwt"], token.jwt);
	postMessage({ method: "tokenUpdated", token });
}

function canPostToNativeMobileApp() {
	return hasNativeMobileBridge();
}

export function sendDebugLogMessage(label, meta = {}) {
	if (!canPostToNativeMobileApp()) {
		return false;
	}

	return postMessage({
		method: "debugLog",
		label,
		meta,
		source: "cloud-webview",
		timestamp: Date.now()
	});
}

export function sendLogoutMessage(reason = "", meta = {}) {
	postMessage({
		method: "logout",
		reason,
		meta,
		source: "cloud-webview",
		timestamp: Date.now()
	});
}

export function sendLogoutCompleteMessage(reason = "", meta = {}) {
	postMessage({
		method: "logoutComplete",
		reason,
		meta,
		source: "cloud-webview",
		timestamp: Date.now()
	});
}

export function sendHideModalEditorMessage() {
	postMessage({ method: "hideModalEditor" });
}

export function sendDummyMessage() {
	postMessage({ method: "dummy" });
}

export function sendSaveInCameraRollMessage(content, fileName) {
	postMessage({
		method: "saveInCameraRoll",
		content,
		fileName
	});
}

export function sendRestoreSession(restore) {
	postMessage({ method: "restoreSession", restore });
}

export function sendLoginStartedMessage() {

}

export function sendLoginFinishedMessage() {

}

export function sendSyncStartMessage() {

}

export function sendSyncFinishedMessage() {

}

let embedded = false;

export function setEmbeddedAppState(_embedded) {
	embedded = _embedded;
}


export function postMessage(message, embed = embedded) {
	let success = false;


	if (typeof window === "undefined") {
		return false;
	}

	if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
		try {
			// console.log("About to post message", message, embed ? "*" : window.location.href);
			// const target = embed ? parent : window; //eslint-disable-line
			// target.ReactNativeWebView.postMessage(JSON.stringify(message), embed ? "*" : window.location.href);
			window.ReactNativeWebView.postMessage(JSON.stringify(message));
			success = true;
		} catch (e) {
			console.error(e);
		}
	} else if (window.webView && typeof window.webView.postMessage === "function") {
		try {
			window.webView.postMessage(JSON.stringify(message));
			success = true;
		} catch (e) {
			console.error(e);
		}
	} else if (typeof window.postMessage === "function") {
		try {
			console.log("About to post message", message, embed ? "*" : window.location.href);
			const target = embed ? parent : window; //eslint-disable-line
			target.postMessage(JSON.stringify(message), embed ? "*" : window.location.href);
			success = true;
		} catch (e) {
			console.error(e);
		}
	}

	return success;
}


function getUserInfo() {
	const user = state.get(["user"]);
	return {
		username: getUsername(),
		scr: state.get(["user", "password"]),
		savePassword: getLs(state.get(["user", "username"]) + ";savePassword"),
		rememberMe: getLs(state.get(["user", "username"]) + ";rememberMe"),
		identityId: getIdentityId(),
		subscriptionPlan: user.subscriptionPlan,
		subscriptionExpiration: user.hasOwnProperty("subscriptionExpiration") ? user.subscriptionExpiration : 0,
		member: state.get(["selectedOrg", "member"])
	};
}
